import { Position, PositionAnalysis, SellTier, DEFAULT_SELL_TIERS, AlertLevel, ProfitTarget, StopLoss } from '@/types/portfolio';
import { ExchangeRates, convertToCHF } from './currencyApi';

// Calculate profit targets based on reward-to-risk ratios
function calculateProfitTargets(buyPrice: number, gainPct: number, currentPrice: number): ProfitTarget[] {
  const targets: ProfitTarget[] = [];
  const riskPct = 5; // Using 5% as base risk for reward calculations
  
  // 2:1 Reward:Risk (10% gain target)
  const target2to1 = buyPrice * 1.10;
  targets.push({
    ratio: '2:1',
    targetPrice: target2to1,
    targetGainPct: 10,
  });
  
  // 3:1 Reward:Risk (15% gain target)
  const target3to1 = buyPrice * 1.15;
  targets.push({
    ratio: '3:1',
    targetPrice: target3to1,
    targetGainPct: 15,
  });
  
  // 4:1 Reward:Risk (20% gain target)
  const target4to1 = buyPrice * 1.20;
  targets.push({
    ratio: '4:1',
    targetPrice: target4to1,
    targetGainPct: 20,
  });
  
  // 6:1 Reward:Risk (30% gain target)
  const target6to1 = buyPrice * 1.30;
  targets.push({
    ratio: '6:1',
    targetPrice: target6to1,
    targetGainPct: 30,
  });
  
  return targets;
}

// Calculate stop-loss levels
function calculateStopLoss(buyPrice: number, currentPrice: number): StopLoss[] {
  const stopLosses: StopLoss[] = [];
  
  // Percentage-based stops (5% and 10%)
  stopLosses.push({
    type: 'percentage',
    price: buyPrice * 0.95,
    description: '5% below entry (tight stop)',
  });
  
  stopLosses.push({
    type: 'percentage',
    price: buyPrice * 0.90,
    description: '10% below entry (standard stop)',
  });
  
  // Technical stop (approximation - below recent swing low)
  const technicalStop = buyPrice * 0.92;
  stopLosses.push({
    type: 'technical',
    price: technicalStop,
    description: 'Below recent swing low',
  });
  
  // Moving average stops (approximations)
  stopLosses.push({
    type: 'ma20',
    price: currentPrice * 0.97,
    description: '20-day MA trailing stop',
  });
  
  stopLosses.push({
    type: 'ma50',
    price: currentPrice * 0.95,
    description: '50-day MA trailing stop',
  });
  
  return stopLosses;
}

// Determine alert level and action
function determineAlertLevel(gainPct: number): AlertLevel {
  const gainPctValue = gainPct * 100;
  
  if (gainPctValue >= 45) {
    return {
      type: 'danger',
      color: 'bg-red-500',
      label: '🎯 TAKE PROFIT',
      action: 'Sell 50% immediately to lock in 100% capital recovery',
    };
  } else if (gainPctValue >= 30) {
    return {
      type: 'warning',
      color: 'bg-orange-500',
      label: '⚡ PROFIT ZONE',
      action: 'Consider partial profit-taking (40% recovery target)',
    };
  } else if (gainPctValue >= 15) {
    return {
      type: 'profit',
      color: 'bg-yellow-500',
      label: '💰 PROFIT ALERT',
      action: 'Monitor for profit-taking opportunity (20% recovery)',
    };
  } else if (gainPctValue < -5) {
    return {
      type: 'danger',
      color: 'bg-red-600',
      label: '🛑 STOP LOSS',
      action: 'Consider cutting losses - below 5% threshold',
    };
  } else {
    return {
      type: 'hold',
      color: 'bg-gray-400',
      label: 'HOLD',
      action: 'Monitor position and wait for profit targets',
    };
  }
}

export function analyzePosition(
  position: Position,
  currentPrice: number,
  exchangeRates: ExchangeRates,
  sellTiers: SellTier[] = DEFAULT_SELL_TIERS
): PositionAnalysis {
  const investedValue = position.buyPrice * position.quantity;
  const totalValue = currentPrice * position.quantity;
  const gainAmount = totalValue - investedValue;
  const gainPct = (gainAmount / investedValue) * 100;
  
  // Find the highest tier that qualifies
  let cumulativeRecoverPct = 0;
  let amountToRecover = 0;
  let matchedTier = null;
  
  for (const tier of sellTiers.sort((a, b) => b.gainPct - a.gainPct)) {
    if (gainPct >= tier.gainPct) {
      matchedTier = tier;
      cumulativeRecoverPct = tier.cumulativeRecoverPct;
      amountToRecover = investedValue * (cumulativeRecoverPct / 100);
      console.log(`${position.ticker}: Gain=${gainPct.toFixed(2)}%, Tier=${tier.gainPct}%, RecoverPct=${cumulativeRecoverPct}%, Amount=${amountToRecover.toFixed(2)}`);
      break;
    }
  }
  
  // Calculate shares to sell - use direct percentage if specified, otherwise calculate from recovery amount
  let recommendedSellQty = 0;
  if (matchedTier?.sellQtyPct !== undefined) {
    // Direct percentage of shares
    recommendedSellQty = Math.floor(position.quantity * (matchedTier.sellQtyPct / 100));
    console.log(`${position.ticker}: Using direct sellQtyPct=${matchedTier.sellQtyPct}%, SellQty=${recommendedSellQty}`);
  } else if (amountToRecover > 0) {
    // Calculate from recovery amount
    recommendedSellQty = Math.floor(amountToRecover / currentPrice);
  }
  
  const recommendedSellPct = position.quantity > 0
    ? Math.round((recommendedSellQty / position.quantity) * 100)
    : 0;
  
  console.log(`${position.ticker}: SellQty=${recommendedSellQty}, SellPct=${recommendedSellPct}%, CurrentPrice=${currentPrice}`);
  
  // Convert total value to CHF
  const currency = position.localCurrency || 'CHF';
  const totalValueCHF = convertToCHF(totalValue, currency, exchangeRates);
  const capitalRecovered = amountToRecover;
  const capitalRecoveredCHF = convertToCHF(capitalRecovered, currency, exchangeRates);
  const capitalRecoveredPct = cumulativeRecoverPct;
  const remainingCapitalAtRisk = Math.max(0, investedValue - capitalRecovered);
  
  // Calculate alert level and trading strategy
  const alertLevel = determineAlertLevel(gainPct / 100);
  const profitTargets = calculateProfitTargets(position.buyPrice, gainPct, currentPrice);
  const stopLoss = calculateStopLoss(position.buyPrice, currentPrice);
  
  // Calculate current risk-reward ratio
  const nextProfitTarget = profitTargets.find(t => currentPrice < t.targetPrice);
  const riskRewardRatio = nextProfitTarget 
    ? `${nextProfitTarget.ratio} (Target: ${nextProfitTarget.targetPrice.toFixed(2)})` 
    : 'Target reached';
  
  return {
    ...position,
    currentPrice,
    gainPct: gainPct / 100, // Store as decimal for consistent display
    gainAmount: Math.round(gainAmount * 100) / 100,
    recommendedSellQty,
    recommendedSellPct,
    totalValue: Math.round(totalValue * 100) / 100,
    totalValueCHF: Math.round(totalValueCHF * 100) / 100,
    investedValue: Math.round(investedValue * 100) / 100,
    capitalRecovered: Math.round(capitalRecovered * 100) / 100,
    capitalRecoveredCHF: Math.round(capitalRecoveredCHF * 100) / 100,
    capitalRecoveredPct,
    remainingCapitalAtRisk: Math.round(remainingCapitalAtRisk * 100) / 100,
    alertLevel,
    profitTargets,
    stopLoss,
    actionRecommendation: alertLevel.action,
    riskRewardRatio,
  };
}

export function calculatePortfolioStats(analyses: PositionAnalysis[], exchangeRates: ExchangeRates) {
  const totalInvested = analyses.reduce((sum, pos) => sum + pos.investedValue, 0);
  const totalValue = analyses.reduce((sum, pos) => sum + pos.totalValue, 0);
  const totalGain = totalValue - totalInvested;
  const totalGainPct = totalInvested > 0 ? (totalGain / totalInvested) * 100 : 0;
  
  // Calculate totals in CHF
  const totalValueCHF = analyses.reduce((sum, pos) => sum + (pos.totalValueCHF || 0), 0);
  const totalInvestedCHF = analyses.reduce((sum, pos) => {
    const currency = pos.localCurrency || 'CHF';
    return sum + convertToCHF(pos.investedValue, currency, exchangeRates);
  }, 0);
  const totalAddedValueCHF = analyses.reduce((sum, pos) => sum + (pos.addedValueChf || 0), 0);
  const initialTotalInvestment = totalValueCHF - totalAddedValueCHF;
  
  return {
    totalInvested: Math.round(totalInvested * 100) / 100,
    totalValue: Math.round(totalValue * 100) / 100,
    totalValueCHF: Math.round(totalValueCHF * 100) / 100,
    totalInvestedCHF: Math.round(totalInvestedCHF * 100) / 100,
    totalAddedValueCHF: Math.round(totalAddedValueCHF * 100) / 100,
    initialTotalInvestment: Math.round(initialTotalInvestment * 100) / 100,
    totalGain: Math.round(totalGain * 100) / 100,
    totalGainPct: Math.round(totalGainPct * 100) / 100,
    positionCount: analyses.length
  };
}
